import React, { FC, ReactNode } from "react";
import { ReactComponent as InfoIcon } from "assets/images/iconsV2/info-circle.svg";
import { ReactComponent as CloseIcon } from "assets/images/iconsV2/x-close.svg";
import { ReactComponent as AlertTriangle } from "assets/images/iconsV2/alert-triangle.svg";
import { ReactComponent as CheckCircle } from "assets/images/iconsV2/check-circle-green.svg";
import { ReactComponent as AlertCircle } from "assets/images/iconsV2/alert-circle.svg";
import "./Alert.scss";
import Button from "../Button/Button";

/**
 * Available colors for the Alert component.
 */
export type AlertColor = "default" | "brand" | "error" | "warning" | "success" | "gray";

/**
 * Props for the Alert component.
 */
export type AlertProps = {
  /**
   * The color variant of the alert, which determines the styling and icon. Must be one of the defined `AlertColor`.
   */
  variant?: AlertColor;
  /**
   * The label text displayed at the top of the alert.
   */
  label: string;
  /**
   * The content or description displayed inside the alert.
   */
  children: React.ReactNode;
  /**
   * Optional icon to be displayed if provided.
   */
  icon?: ReactNode;
  /**
   * Optional callback function to be invoked when the close icon is clicked.
   * If not provided, the `onDismiss` function will be called instead.
   */
  onClose?: () => void;
  /**
   * Optional callback function to be invoked when the dismiss button is clicked.
   */
  onDismiss?: () => void;
  /**
   * Optional callback function to be invoked when the "View changes" button is clicked.
   */
  onViewChanges?: () => void;
  /**
   * Optional custom action to render
   */
  customActions?: ReactNode;
};

/**
 * Alert component renders a customizable alert with different visual variants.
 * It supports optional dismiss and action buttons.
 *
 * @param {AlertProps} props - The properties object for the Alert component.
 * @returns {JSX.Element} - A styled alert element with optional action buttons.
 */

const Alert: FC<AlertProps> = function (props) {
  const { variant, children, label, icon, onDismiss, onViewChanges, onClose, customActions } = props;

  return (
    <div className={`v2-custom-alert ${variant} w-100`}>
      {/* Render icon based on the alert variant */}
      {variant === "warning" && <AlertTriangle />}
      {variant === "success" && <CheckCircle />}
      {variant === "error" && <AlertCircle className={`info-icon ${variant}`} />}
      {(variant === "brand" || variant === "gray") && <InfoIcon className={`info-icon ${variant}`} />}
      {variant === "default" && !icon && (
        <div className="d-flex justify-content-center align-items-center default-icon-box">
          <InfoIcon className={`info-icon ${variant}`} />
        </div>
      )}
      {/* Render a custom icon if provided */}
      {icon && icon}

      <div className="d-flex flex-column v2-alert-container">
        <div className="v2-alert-content">
          <div className="d-flex justify-content-between">
            <div className="text-sm text-secondary-v2 font-semibold">{label}</div>
            {(onClose || onDismiss) && (
              <span className="d-flex justify-content-center align-items-center x-close-icon-box cursor-pointer">
                <CloseIcon width={20} height={20} onClick={onClose || onDismiss} />
              </span>
            )}
          </div>
          <div className="text-sm text-secondary-v2 font-regular">{children}</div>
        </div>
        {/* Render buttons if provided */}
        {(onDismiss || onViewChanges || customActions) && (
          <div className="d-flex action-button alert-action-buttons">
            {onDismiss && <Button label="Dismiss" variant="link-gray" size="sm" onClick={onDismiss} />}
            {onViewChanges && <Button label="View changes" variant="link-color" size="sm" onClick={onViewChanges} />}
            {customActions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Alert;

Alert.defaultProps = {
  variant: "default",
  icon: "",
  onClose: undefined,
  onDismiss: undefined,
  onViewChanges: undefined,
  customActions: undefined,
};
